/**
 * @file 积分充值弹窗 - 左侧积分档位选择
 * @description 展示 SKU 卡片
 */

import {useEffect, useState} from 'react';
import styles from './index.module.less';
import Icon from '@/components/common/Icon';
import message from '@/components/common/Message';
import StatusView from '@/components/common/StatusView';
import {createCx} from '@/utils/cx';
import {fenToYuan} from '@/utils/money';
import {getPointsSkuList} from '@/services/commerce/points';
import type {PointsSku} from '@/services/commerce/points/interface';
import type {SkuId} from '@/services/commerce/type';

const cx = createCx(styles);

/** 默认选中的档位（5000 积分） */
const DEFAULT_POINTS_AMOUNT = 5000;

interface PackageListProps {
    /** 当前选中的 SKU */
    selectedSkuId: SkuId | null;
    /** 设置选中态：数据回填默认档位 & 用户点击均会触发 */
    onSelect: (sku: PointsSku) => void;
    /** 用户主动点击时触发（用于点击埋点，默认回填不会触发） */
    onUserSelect?: (sku: PointsSku) => void;
    /** SKU 列表加载完成时向父组件回传，用于展示温馨提示文案 */
    onSkusLoaded?: (skus: PointsSku[]) => void;
    /** 外部触发重新拉取 SKU 列表（价格校验不一致时使用） */
    refreshKey?: number;
}

const PackageList: React.FC<PackageListProps> = ({
    selectedSkuId,
    onSelect,
    onUserSelect,
    onSkusLoaded,
    refreshKey,
}) => {
    const [skus, setSkus] = useState<PointsSku[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        let canceled = false;
        setIsLoading(true);
        void getPointsSkuList()
            .then((res) => {
                if (canceled) {
                    return;
                }
                setSkus(res.points);
            })
            .catch((error: unknown) => {
                void message.error('获取积分档位失败');
                console.error('获取SKU 列表失败:', error);
            })
            .finally(() => {
                if (canceled) {
                    return;
                }
                setIsLoading(false);
            });
        return () => {
            canceled = true;
        };
    }, [refreshKey]);

    // 数据回填时选中默认档位
    useEffect(() => {
        if (skus.length === 0 || selectedSkuId !== null) {
            return;
        }
        const defaultSku = skus.find((s) => s.pointsAmount === DEFAULT_POINTS_AMOUNT) ?? skus[0];
        onSelect(defaultSku);
    }, [skus, selectedSkuId, onSelect]);

    useEffect(() => {
        if (skus.length > 0) {
            onSkusLoaded?.(skus);
        }
    }, [skus, onSkusLoaded]);

    if (isLoading) {
        return (
            <div className={cx('placeholder')}>
                <StatusView status="loading" size="medium" loadingTheme="white" />
            </div>
        );
    }

    return (
        <div className={cx('root')}>
            {skus.map((sku) => {
                const selected = sku.skuId === selectedSkuId;
                const showFirstChargeTag = sku.isFirstCharge && sku.bonusPoints > 0;
                return (
                    <button
                        key={sku.skuId}
                        type="button"
                        className={cx('card', {cardSelected: selected})}
                        onClick={() => {
                            onSelect(sku);
                            onUserSelect?.(sku);
                        }}
                    >
                        {showFirstChargeTag && (
                            <div className={cx('firstChargeTag')}>
                                首充送 {sku.bonusPoints} 积分
                            </div>
                        )}
                        <div className={cx('points')}>
                            <Icon name="AijBasicJifen" size={22} className={cx('pointsIcon')} />
                            <span className={cx('pointsAmount')}>{sku.pointsAmount}</span>
                        </div>
                        <div className={cx('price')}>¥{fenToYuan(sku.sellPrice)}</div>
                    </button>
                );
            })}
        </div>
    );
};

export default PackageList;
